'use client'

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { IChildrenProps } from '@/utils/typings/children-props'
import { createSafeFallback } from '@/utils/functions/create-safe-fallback'

export type IResearchAdsConfig = {
    /**
     * Shows the footer's save-to-board button. Off on a board's own detail page, where every ad
     * is already on the board and the card has no membership signal to make the label truthful.
     */
    allowSaveToBoard: boolean
    /**
     * Read-only surfaces (the public board page and the in-app shared-board viewer). Hides every
     * authenticated affordance — hover actions, selection, and the preview modal — and renders
     * media from the raw signed URL rather than through `createImageUrl`, because appending query
     * params to a CloudFront-signed URL breaks its signature (a 403). The card becomes a static
     * display plus, when `buildRecreateHref` is set, a public Recreate CTA.
     */
    readOnly: boolean
    /**
     * Read-only Recreate CTA, in-app variant. When set, the card dispatches the composer intent
     * directly — used by the authenticated shared-board viewer so it doesn't bounce a signed-in
     * user through the anonymous full-page deep link. Takes precedence over `buildRecreateHref`.
     */
    onRecreate?: (adUuid: string) => void
    /**
     * Read-only Recreate CTA, anonymous variant: a public deep link that routes a signed-out
     * visitor through sign-up into the composer. Used when `onRecreate` is not provided.
     */
    buildRecreateHref?: (adUuid: string) => string
    /**
     * Board-detail surface: a secondary "Remove from {boardName}" footer action beside
     * Recreate. The handler owns the mutation; the card stays presentation-only.
     */
    removeFromBoard?: { boardName: string; onRemove: (adUuid: string) => void }
    /**
     * Company-scoped surfaces (the company page's library) pin the feed to one advertiser, so
     * the org-level empty states ("add your first competitor", "researching…") are wrong there.
     * When set, this node replaces every empty state.
     */
    companyEmptyState?: ReactNode
}

interface IResearchAdsContext {
    config: IResearchAdsConfig
}

const DEFAULT_CONFIG: IResearchAdsConfig = {
    allowSaveToBoard: true,
    readOnly: false,
}

const ResearchAdsContext = createContext<IResearchAdsContext | null>(null)

type IResearchAdsProviderProps = IChildrenProps & { config?: Partial<IResearchAdsConfig> }

export function ResearchAdsProvider({ children, config }: IResearchAdsProviderProps) {
    const mergedConfig = useMemo<IResearchAdsConfig>(() => ({ ...DEFAULT_CONFIG, ...config }), [config])

    return (
        <ResearchAdsContext.Provider value={useMemo(() => ({ config: mergedConfig }), [mergedConfig])}>
            {children}
        </ResearchAdsContext.Provider>
    )
}

export function useResearchAdsContext() {
    const context = useContext(ResearchAdsContext)

    if (!context) {
        if (process.env.NODE_ENV !== 'production') {
            throw new Error('useResearchAdsContext must be used within a ResearchAdsContextProvider')
        }

        return createSafeFallback({}) as IResearchAdsContext
    }

    return context
}
